/**
 * The chain that gives a node its meaning — scenario, variable, then every
 * consequence down to the node — in the shape the Did you mean? step and
 * the AI suggestion endpoint read. Shared by both map screens.
 */
import { getScenarioContext } from '../../config/scenarioDescriptions';

export function buildPathChain(targetNode, pathways, scenarioInfo, language = 'fi') {
  if (!targetNode || !pathways || pathways.length === 0) {
    return [];
  }

  // Create node lookup map
  const nodeMap = {};
  pathways.forEach(node => {
    nodeMap[node.id] = node;
  });

  // Traverse up from target to root, collecting nodes
  const pathNodes = [];
  let currentNode = targetNode;

  while (currentNode) {
    pathNodes.unshift({
      id: currentNode.id,
      title: currentNode.title || currentNode.text || 'Untitled',
      description: currentNode.description || '',
      type: currentNode.is_scenario_starter ? 'variable' : 'consequence',
      depth: currentNode.depth
    });

    // Move to parent
    const parentId = currentNode.parentId || currentNode.parent_id;
    if (parentId && parentId !== 'instance-root') {
      currentNode = nodeMap[parentId];
    } else {
      currentNode = null;
    }
  }

  // Build the full chain starting with scenario context
  const chain = [];

  // Add scenario context from static descriptions (richer info than just title)
  if (scenarioInfo?.title) {
    const scenarioContext = getScenarioContext(scenarioInfo.title, null, language);
    chain.push({
      type: 'scenario',
      title: scenarioInfo.title,
      description: scenarioContext?.scenarioDescription || scenarioInfo.description || ''
    });
  }

  // Find the variable (first node in path, depth 0)
  const variableNode = pathNodes.find(n => n.depth === 0 || n.type === 'variable');
  if (variableNode) {
    // Prefer database description (future-specific) over static config (generic)
    const scenarioContext = getScenarioContext(scenarioInfo?.title, variableNode.title, language);
    chain.push({
      type: 'variable',
      title: variableNode.title,
      description: variableNode.description || scenarioContext?.variableDescription || ''
    });
  }

  // Add consequence nodes (depth > 0)
  pathNodes.forEach(node => {
    if (node.depth > 0 && node.type !== 'variable') {
      chain.push({
        type: 'consequence',
        title: node.title,
        description: node.description || ''
      });
    }
  });

  return chain;
}
